import { cn } from '@chopchop-se/ui';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';

export interface TabDef {
  id: string;
  label: string;
  badge?: number;
}

/**
 * Detail-page tabs kept in the URL (`?tab=history`).
 *
 * Same reasoning as `useListState`: a supervisor pastes "the Historik tab of
 * this article" into Slack and it opens there, and the back button out of a tab
 * behaves. `replace` keeps tab switching out of the history stack, so Back
 * leaves the page instead of walking the tabs backwards.
 */
export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: readonly TabDef[];
  active: string;
  onChange: (id: string) => void;
}): ReactNode {
  return (
    <div role="tablist" className="mb-5 flex flex-wrap gap-1 border-b border-surface-border">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          onClick={() => {
            onChange(tab.id);
          }}
          className={cn(
            '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
            tab.id === active
              ? 'border-brand-600 text-ink'
              : 'border-transparent text-ink-muted hover:text-ink',
          )}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 ? (
            <span className="ml-2 rounded-full bg-surface-subtle px-1.5 py-0.5 font-mono text-xs text-ink-muted">
              {tab.badge}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function useTabState(defaultTab: string): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams();
  const active = params.get('tab') ?? defaultTab;
  const setActive = (id: string): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id === defaultTab) next.delete('tab');
        else next.set('tab', id);
        return next;
      },
      { replace: true },
    );
  };
  return [active, setActive];
}
